import locale from "@bonliva-crm/locale";
import { SingleSelect } from "@bonliva-crm/ui";
import { getSortOptionsNew } from "@search";
import { ComponentProps, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { ConsultantFilterState } from "../../utils/consultant-filter-state";
import {
  mapSortOptionKeyToValue,
  mapSortValueToOptionKey
} from "../../utils/sort-option-mapping";

type Option = { label: string; value: string };

/**
 * Sort dropdown for the consultant search top bar (Relevans, Namn A-Ö, ...).
 *
 * Mirrors the generic `SortFilter` UI but bridges its string-based options to
 * the object-shaped `sort` value used by the consultant search filter state, so
 * the selection reaches the GraphQL query via `use-consultant-table-datasource`.
 */
export const ConsultantSortFilter = () => {
  const sortOptions = useMemo(() => getSortOptionsNew(), []);

  const { control, setValue } = useFormContext<ConsultantFilterState>();
  const { field } = useController({ name: "sort", control });

  const selectedKey = mapSortValueToOptionKey(field?.value);

  const value: Option = {
    label: sortOptions.find((option) => option.value === selectedKey)?.label,
    value: selectedKey
  };

  const commonProps: ComponentProps<typeof SingleSelect<Option>> = {
    options: sortOptions,
    name: "sort",
    value,
    onChange: (selected) => {
      setValue("sort", mapSortOptionKeyToValue(selected.value), { shouldDirty: true });
    },
    hideSelectedOptions: false,
    controlShouldRenderValue: true,
    placeholder: locale.SortBy1,
    isSearchable: false,
    maxMenuHeight: 600,
    showBadge: false,
    optionType: "radio",
    isClearable: false,
    menuWidth: "280px"
  };

  return (
    <SingleSelect<Option>
      {...commonProps}
      styleProps={{
        control: { showBorder: true },
        menu: { showBorder: true }
      }}
    />
  );
};
